import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Sparkles, type LucideIcon } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { useCoachRail } from "@/context/CoachRailContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";
import { shortcutLabel } from "@/lib/keyboard";

type PaletteNavCommand = {
  id: string;
  label: string;
  url: string;
  icon: LucideIcon;
};

/**
 * The ⌘K command palette (2b) — the AI's front door "from anywhere". It both
 * searches (jump to any screen) and asks: typing a question and pressing ⌘↵
 * (or the Ask row) hands it to the coach rail via `openWith`. ⌘J starts a new
 * coach thread. Mounted once in `AppLayout`; owns the global ⌘K shortcut.
 */
export function CommandPalette() {
  const { t } = useTranslation("intelligence");
  const { t: tNav } = useTranslation("nav");
  const navigate = useNavigate();
  const entitled = useIntelligenceAccess();
  const features = useVisibleSidebarNavFeatures();

  const {
    paletteOpen,
    openPalette,
    closePalette,
    togglePalette,
    openWith,
    openRail,
    newThread,
  } = useCoachRail();

  const [query, setQuery] = useState("");
  const askShortcut = shortcutLabel("↵");
  const newThreadShortcut = shortcutLabel("J");

  // Global ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    if (!entitled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        togglePalette();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [entitled, togglePalette]);

  // Reset the query each time the palette opens.
  useEffect(() => {
    if (paletteOpen) setQuery("");
  }, [paletteOpen]);

  const navCommands = useMemo<PaletteNavCommand[]>(() => {
    return features.flatMap((f) => {
      const parentLabel = tNav(f.titleKey);
      // Every visible child is one keystroke away: the tabs of a tabbed page
      // ("Teams › Tests") and the pages behind the Settings gear ("Settings ›
      // Users"). A tabbed page's index tab is the page itself, so it is skipped.
      const childRows = (f.children ?? [])
        .filter((c) => c.url !== f.url)
        .map((c) => ({
          id: `${f.id}:${c.url}`,
          label: `${parentLabel} › ${tNav(c.titleKey)}`,
          url: c.url,
          icon: f.icon,
        }));
      if (f.url && !f.hiddenFromNav) {
        return [
          { id: f.id, label: parentLabel, url: f.url, icon: f.icon },
          ...childRows,
        ];
      }
      return childRows;
    });
  }, [features, tNav]);

  const filteredNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navCommands;
    return navCommands.filter((c) => c.label.toLowerCase().includes(q));
  }, [navCommands, query]);

  if (!entitled) return null;

  const trimmed = query.trim();

  const ask = () => {
    closePalette();
    if (trimmed) openWith(trimmed);
    else openRail();
  };

  const startNewThread = () => {
    closePalette();
    newThread();
    openRail();
  };

  const goTo = (url: string) => {
    closePalette();
    navigate(url);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      ask();
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
      e.preventDefault();
      startNewThread();
    }
  };

  return (
    <Dialog
      open={paletteOpen}
      onOpenChange={(o) => (o ? openPalette() : closePalette())}
    >
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 overflow-hidden p-0 sm:max-w-[560px]"
      >
        <DialogTitle className="sr-only">{t("palette.title")}</DialogTitle>
        <Command
          label={t("palette.title")}
          shouldFilter={false}
          onKeyDown={handleKeyDown}
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t("palette.placeholder")}
          />
          <CommandList>
            <CommandGroup heading={t("palette.ask.heading")}>
              <CommandItem value="__ask__" onSelect={ask} className="gap-2">
                <Sparkles className="text-primary" aria-hidden />
                <span className="min-w-0 flex-1 truncate">
                  {trimmed
                    ? t("palette.ask.withQuery", { query: trimmed })
                    : t("palette.ask.empty")}
                </span>
                <CommandShortcut>{askShortcut}</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="__new__"
                onSelect={startNewThread}
                className="gap-2"
              >
                <Plus className="text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1 truncate">
                  {t("palette.newThread")}
                </span>
                <CommandShortcut>{newThreadShortcut}</CommandShortcut>
              </CommandItem>
            </CommandGroup>

            {filteredNav.length > 0 && (
              <CommandGroup heading={t("palette.goto")}>
                {filteredNav.map(({ id, label, url, icon: Icon }) => (
                  <CommandItem
                    key={id}
                    value={`nav:${id}`}
                    onSelect={() => goTo(url)}
                    className="gap-2"
                  >
                    <Icon className="text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>

          <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            <PaletteHint keys={askShortcut} label={t("palette.hint.ask")} />
            <PaletteHint
              keys={newThreadShortcut}
              label={t("palette.hint.new")}
            />
            <PaletteHint keys="esc" label={t("palette.hint.close")} />
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function PaletteHint({ keys, label }: { keys: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <kbd className="pointer-events-none select-none rounded border border-border bg-muted px-1.5 font-mono text-[10.5px] font-medium">
        {keys}
      </kbd>
      {label}
    </span>
  );
}
